import store from './store';

// Is anyone using this tab? App.vue calls startActivityTracking() once. It keeps $store.state.lastActivityAt (ms) up
// to date from clicks, typing, scrolling, touches and pointer movement, and notices when a hidden tab comes back.
// Pages use whenIdle() to do something once the tab has been left alone (e.g. Meetings.vue refreshing its list).
// Kept in the store rather than localStorage on purpose: it's per tab, so another busy tab doesn't keep this one "active".

const MOVE_THROTTLE_MS = 5000; // pointer movement is constant, so it only counts this often
let started = false;
let hiddenAt: number | null = null;
const returnListeners = new Set<(awayMs: number) => void>();

function markActive() {
    store.state.lastActivityAt = Date.now();
}

function markMoved() {
    if(Date.now() - store.state.lastActivityAt > MOVE_THROTTLE_MS) {
        markActive();
    }
}

export function startActivityTracking() {
    if(started) {
        return;
    }
    started = true;
    markActive();
    for(const type of [ 'mousedown', 'keydown', 'touchstart', 'wheel' ]) {
        document.addEventListener(type, markActive, { capture: true, passive: true });
    }
    // scroll doesn't bubble; capture catches scrolling inside any container
    document.addEventListener('scroll', markActive, { capture: true, passive: true });
    document.addEventListener('pointermove', markMoved, { capture: true, passive: true });
    document.addEventListener('visibilitychange', () => {
        if(document.hidden) {
            hiddenAt = Date.now();
            return;
        }
        const awayMs = hiddenAt ? Date.now() - hiddenAt : 0;
        hiddenAt = null;
        returnListeners.forEach(listener => listener(awayMs));
    });
}

// Calls callback when the tab has had no activity for idleMs (checked every minute, then again every idleMs while it
// stays idle), and when the tab comes back into view after being hidden for at least idleMs. Nothing runs while the
// tab is hidden. Returns a function that stops it; call that from the page's beforeUnmount.
export function whenIdle(idleMs: number, callback: () => void): () => void {
    let lastFired = 0;
    const check = () => {
        const now = Date.now();
        if(!document.hidden && now - Math.max(lastFired, store.state.lastActivityAt) >= idleMs) {
            lastFired = now;
            callback();
        }
    };
    const onReturn = (awayMs: number) => {
        if(awayMs >= idleMs) {
            lastFired = Date.now();
            callback();
        }
    };
    const timer = setInterval(check, 60000);
    returnListeners.add(onReturn);
    return () => {
        clearInterval(timer);
        returnListeners.delete(onReturn);
    };
}
